import { useState } from 'react'
import { useAuth } from '@praxis/component-auth'
import { Container } from 'components/container/Container'
import { useUser } from 'components/user-context'
import { UrlParams } from 'enums/url-params'
import { useSalesLocation } from 'hooks/sales/useSalesLocation'
import { useSearchParams } from 'react-router-dom'
import { useMPMEnv } from 'utils/env'
import { BasicPlaceholder } from 'components/placeholder'
import { ErrorAlert } from 'components/error-fallback'
import { Cell, TableData, addHyperlinkToFirstColumn } from 'utils/data'
import { useSalesLocationHistorical } from 'hooks/sales/useSalesLocationHistorical'
import {
    getGreenfieldDrillValues,
    getGreenfieldFilterValues,
} from 'utils/greenfield'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import useRefresh from 'hooks/utils/useRefresh'
import useCountdown from 'hooks/utils/useCountdown'
import { Constants } from 'constants/constants'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { translateTableData } from 'locales/utils'
import { format } from 'date-fns'

export const SalesLocation = () => {
    const { t, isEnglish } = useEssentials()
    const { sales: salesKeys } = TRANSLATION_KEYS
    const filtersKeys = salesKeys.filters
    const timePeriods = salesKeys.timePeriods
    const [params] = useSearchParams()
    const { location } = useUser()
    const env = useMPMEnv()
    const { logout, session } = useAuth()
    const currentLocation =
        params.get(UrlParams.LOCATION_ID) ?? location.shortName

    const [timePeriodIndex, setTimePeriodIndex] = useState<number>(0)
    const [tabsIndex, setTabsIndex] = useState<number>(0)

    const columnWidths = isEnglish
        ? [70, 80, 90, 80, 80]
        : [55, 70, 120, 80, 140]

    const today = useSalesLocation({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        dynamicSubPath: Constants.GREENFIELD_CARDS.SALES.LOCATION_INTRADAY,
        filters: {
            ...(getGreenfieldDrillValues(currentLocation) || {}),
            ...getGreenfieldFilterValues(
                'SALES_DATE',
                format(new Date(), 'yyyy-MM-dd'),
            ),
        },
    })
    const result = useSalesLocationHistorical({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        dynamicSubPath: Constants.GREENFIELD_CARDS.SALES.LOCATION_HISTORICAL,
        filters: getGreenfieldDrillValues(currentLocation),
    })
    const results = [today, ...result]
    results.forEach((result) => {
        if (result.data) {
            result.data = addHyperlinkToFirstColumn(
                result.data,
                currentLocation,
            )
            result.data = translateTableData({
                t,
                table: result.data,
                translation: salesKeys.location.tabs,
            })
        }
    })
    // Only enable pull-to-refresh for "today" time period (index 0)
    useRefresh(today.refetch, today.remove, timePeriodIndex === 0)
    const count = useCountdown(5, logout, today.error)

    if (results.some((d) => d.isLoading)) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={10} columns={3} />
            </div>
        )
    }
    const firstError = results.find((d) => d.isError)
    if (firstError) {
        return (
            <ErrorAlert
                error={firstError.error}
                count={count}
                onRetry={() => firstError.refetch()}
            />
        )
    }

    const pickColumns = (
        data: TableData,
        columnIndexes: number[],
    ): TableData => {
        const newData = { ...data }
        newData.headings = columnIndexes
            .map((index) => data.headings?.[index])
            .filter((heading): heading is Cell => heading !== undefined)
        newData.rows = newData.rows.map((row) =>
            columnIndexes.map((index) => row[index]),
        )
        newData.footer = columnIndexes
            .map((index) => data.footer?.[index])
            .filter((heading): heading is Cell => heading !== undefined)

        return newData
    }

    const forecast = t(salesKeys.location.tabs.forecast)
    const compPercent = t(salesKeys.location.tabs.compPercent)
    const payload = {
        [t(filtersKeys.viewInDollars)]: [
            {
                title: t(timePeriods.today),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(today.data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(today.data, [0, 1, 6, 7]),
                },
            },
            {
                title: t(timePeriods.yesterday),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(result[0].data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(result[0].data, [0, 1, 6, 7, 8]),
                },
            },
            {
                title: t(timePeriods.weekToDate),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(result[1].data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(result[1].data, [0, 1, 6, 7, 8]),
                },
            },
            {
                title: t(timePeriods.lastWeek),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(result[2].data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(result[2].data, [0, 1, 6, 7, 8]),
                },
            },
            {
                title: t(timePeriods.monthToDate),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(result[3].data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(result[3].data, [0, 1, 6, 7, 8]),
                },
            },
            {
                title: t(timePeriods.lastMonth),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(result[4].data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(result[4].data, [0, 1, 6, 7, 8]),
                },
            },
            {
                title: t(timePeriods.yearToDate),
                subtitle: '',
                data: {
                    [forecast]: pickColumns(result[5].data, [0, 1, 4, 5]),
                    [compPercent]: pickColumns(result[5].data, [0, 1, 6, 7, 8]),
                },
            },
        ],
    }

    const handleClick = (timePeriodIndex: number, tabsIndex: number) => {
        setTimePeriodIndex(timePeriodIndex)
        setTabsIndex(tabsIndex)
    }

    return (
        <Container role="main">
            <GeneralizedComponent
                payload={payload}
                title={t(salesKeys.location.title)}
                scrollableWidth={true}
                sortable={false}
                widths={columnWidths}
                pinFirstColumn={true}
                parentHandleClick={handleClick}
                selectedTabIndex={tabsIndex}
                selectedTimeIndex={timePeriodIndex}
                kpiName="sales location"
                isTimePeriodSticky={false}
            />
        </Container>
    )
}
